// ============================================================================
// 📊 COMPONENTE - CARDS DE RESUMO FINANCEIRO — V4 LIGHT+DARK ULTRA PREMIUM
// Stripe Dashboard · Apple Health · Nubank App · Vercel Analytics · Linear
// ============================================================================
// 🔥 V4: Soporte completo Light Mode + Dark Mode vía prop isDark
// 🔥 V4: Light: fondos blancos sólidos con bordes suaves y sombras reales
// 🔥 V4: Dark: glassmorphism real con blur y fondos semitransparentes
// 🔥 V4: Contraste WCAG AA garantizado en ambos modos
// 🔥 V4: Textos: gray-700/gray-800 en light, gray-300/gray-400 en dark
// 🔥 V4: Badges: fondos sólidos con opacidad 20-25% en light, 15% en dark
// 🔥 V4: Sparklines: barras vacías visibles en light (#e5e7eb)
// 🔥 V4: Progress bar: bg-gray-200 en light, bg-white/8 en dark
// 🔥 V4: Separadores: text-gray-300 en light, text-white/10 en dark
// 🔥 V4: Shimmer: visible en ambos modos
// ✅ Zero breaking changes — todas as props originais mantidas
// ✅ Zero dependências externas para gráficos (SVG puro)
// ✅ NaN-proof com safeNum em todos os valores
// ✅ Counting animation, sparklines animadas, badges com spring
// ✅ Responsive: 6col desktop → 3col tablet → 2col mobile
// ============================================================================

import React, { useMemo, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Home, Key, TrendingUp, TrendingDown,
  Wallet, Sparkles, ArrowUpRight, ArrowDownRight,
} from "lucide-react";

// ─── constants ────────────────────────────────────────────────────────────────

const spring = { type: "spring", stiffness: 360, damping: 28 };
const springBadge = { type: "spring", stiffness: 500, damping: 22 };

// ─── helpers ──────────────────────────────────────────────────────────────────

const safeNum = (value, fallback = 0) => {
  if (value === null || value === undefined) return fallback;
  const n = Number(value);
  return isNaN(n) || !isFinite(n) ? fallback : n;
};

const safeArr = (arr) => (Array.isArray(arr) ? arr : []);

const fmtBRL = (value, compact = false) => {
  const n = safeNum(value);
  if (compact && Math.abs(n) >= 1_000_000)
    return new Intl.NumberFormat("pt-BR", {
      style: "currency", currency: "BRL", notation: "compact", maximumFractionDigits: 1,
    }).format(n);
  if (compact && Math.abs(n) >= 1_000)
    return new Intl.NumberFormat("pt-BR", {
      style: "currency", currency: "BRL", notation: "compact", maximumFractionDigits: 0,
    }).format(n);
  return new Intl.NumberFormat("pt-BR", {
    style: "currency", currency: "BRL",
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
};

// ─── Counting Number Animation ────────────────────────────────────────────────

const CountingValue = ({ value, compact, className, isDark }) => {
  const [display, setDisplay] = useState(0);
  const raf = useRef(null);
  const start = useRef(null);
  const duration = 900;

  useEffect(() => {
    const target = safeNum(value);
    start.current = null;
    const animate = (ts) => {
      if (!start.current) start.current = ts;
      const elapsed = ts - start.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(target * eased);
      if (progress < 1) raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [value]);

  return <span className={className}>{fmtBRL(display, compact)}</span>;
};

// ─── Mini Sparkline (SVG puro) ────────────────────────────────────────────────

const Sparkline = ({ data = [], color = "#10b981", isMobile, isDark }) => {
  const bars = 6;
  const h = isMobile ? 20 : 28;
  const bw = isMobile ? 2 : 3;
  const gap = 2;
  const w = bars * bw + (bars - 1) * gap;

  const normalized = useMemo(() => {
    const d = safeArr(data).slice(-bars).map((v) => safeNum(v));
    while (d.length < bars) d.unshift(0);
    const max = Math.max(...d, 1);
    return d.map((v) => v / max);
  }, [data]);

  const hasData = normalized.some((v) => v > 0);

  // Color de barras vacías: visible en light (#e5e7eb), sutil en dark
  const emptyColor = isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb";

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      className="overflow-visible"
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id={`spark-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.9" />
          <stop offset="100%" stopColor={color} stopOpacity="0.3" />
        </linearGradient>
      </defs>
      {normalized.map((frac, i) => {
        const barH = hasData ? Math.max(frac * h, 2) : 2;
        const x = i * (bw + gap);
        const y = h - barH;
        return (
          <motion.rect
            key={i}
            x={x}
            y={y}
            width={bw}
            height={barH}
            rx={bw / 2}
            fill={hasData ? `url(#spark-${color.replace("#", "")})` : emptyColor}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ ...spring, delay: 0.1 + i * 0.05 }}
            style={{ transformOrigin: `${x}px ${h}px` }}
          />
        );
      })}
    </svg>
  );
};

// ─── Trend Badge ──────────────────────────────────────────────────────────────

const TrendBadge = ({ direction, percent, isMobile, isDark }) => {
  if (!direction || direction === "stable") return null;
  const up = direction === "up";
  const Icon = up ? ArrowUpRight : ArrowDownRight;

  // Light: fondos más sólidos para visibilidad | Dark: fondos sutiles
  const upBg = isDark ? "bg-emerald-500/15" : "bg-emerald-100";
  const upText = isDark ? "text-emerald-400" : "text-emerald-700";
  const downBg = isDark ? "bg-red-500/15" : "bg-red-100";
  const downText = isDark ? "text-red-400" : "text-red-700";

  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={springBadge}
      className={`inline-flex items-center gap-0.5 font-semibold rounded-full
                  ${isMobile ? "text-[7px] px-1 py-0.5" : "text-[9px] px-1.5 py-0.5"}
                  ${up ? `${upBg} ${upText}` : `${downBg} ${downText}`}`}
    >
      <Icon size={isMobile ? 7 : 9} strokeWidth={2.5} />
      {safeNum(percent, 0).toFixed(1)}%
    </motion.span>
  );
};

// ─── Count Badge ─────────────────────────────────────────────────────────────

const CountBadge = ({ count, colorClass, isMobile }) => (
  <motion.span
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ ...springBadge, delay: 0.1 }}
    className={`font-bold rounded-full tabular-nums
                ${isMobile ? "text-[7px] px-1.5 py-0.5" : "text-[9px] px-2 py-0.5"}
                ${colorClass}`}
  >
    {count}
  </motion.span>
);

// ─── Base Card ────────────────────────────────────────────────────────────────

const Card = ({
  delay, isMobile, isDark, colSpan2 = false, children,
  // Dark mode defaults (glassmorphism)
  glassFromDark = "rgba(255,255,255,0.05)",
  glassToDark = "rgba(255,255,255,0.02)",
  borderColorDark = "rgba(255,255,255,0.08)",
  shadowColorDark = "rgba(0,0,0,0.4)",
  // Light mode defaults (sólido elegante)
  bgFromLight = "#ffffff",
  bgToLight = "#f9fafb",
  borderColorLight = "rgba(0,0,0,0.06)",
  shadowColorLight = "rgba(0,0,0,0.04)",
}) => {
  // Seleccionar paleta según modo
  const bgFrom = isDark ? glassFromDark : bgFromLight;
  const bgTo = isDark ? glassToDark : bgToLight;
  const bColor = isDark ? borderColorDark : borderColorLight;
  const sColor = isDark ? shadowColorDark : shadowColorLight;
  const shimmerColor = isDark ? "via-white/12" : "via-black/5";
  const insetShadow = isDark
    ? "0 1px 0 0 rgba(255,255,255,0.07) inset"
    : "0 1px 0 0 rgba(255,255,255,0.9) inset";

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...spring, delay }}
      whileHover={!isMobile ? { y: -4, scale: 1.02 } : undefined}
      whileTap={isMobile ? { scale: 0.97 } : undefined}
      className={`relative rounded-2xl overflow-hidden cursor-default
                  ${isMobile ? "p-3" : "p-4 md:p-5"}
                  ${colSpan2 ? "col-span-2" : ""}`}
      style={{
        background: `linear-gradient(135deg, ${bgFrom}, ${bgTo})`,
        border: `1px solid ${bColor}`,
        backdropFilter: isDark ? "blur(20px)" : "none",
        WebkitBackdropFilter: isDark ? "blur(20px)" : "none",
        boxShadow: `${insetShadow}, 0 4px 24px -8px ${sColor}`,
        willChange: "transform",
        transition: "box-shadow 0.3s ease, background 0.3s ease",
      }}
    >
      {/* top shimmer — sutil en ambos modos */}
      <div
        className={`absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent ${shimmerColor} to-transparent pointer-events-none`}
      />
      {children}
    </motion.div>
  );
};

// ─── Icon Circle ──────────────────────────────────────────────────────────────

const IconCircle = ({ Icon, color, bg, isMobile }) => (
  <div
    className={`flex items-center justify-center rounded-full shrink-0
                ${isMobile ? "w-6 h-6" : "w-8 h-8"}`}
    style={{ background: bg }}
  >
    <Icon size={isMobile ? 11 : 14} color={color} strokeWidth={2.5} />
  </div>
);

// ─── Progress Bar ─────────────────────────────────────────────────────────────

const ProgressBar = ({ receitas, despesas, isDark }) => {
  const total = safeNum(receitas) + safeNum(despesas);
  const pct = total > 0 ? (safeNum(receitas) / total) * 100 : 50;

  // Light: bg-gray-200 | Dark: bg-white/8
  const trackBg = isDark ? "bg-white/8" : "bg-gray-200";

  return (
    <div className={`w-full h-1.5 rounded-full ${trackBg} overflow-hidden mt-3`}>
      <motion.div
        className="h-full rounded-full"
        style={{
          background: "linear-gradient(90deg, #10b981, #6366f1)",
        }}
        initial={{ width: "0%" }}
        animate={{ width: `${clamp(pct, 2, 98)}%` }}
        transition={{ duration: 1, ease: "easeOut", delay: 0.4 }}
      />
    </div>
  );
};

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

// ─── Main Component ───────────────────────────────────────────────────────────

const SummaryCards = ({
  // ── original props (zero breaking changes) ──────────────────────────────
  isMobile,
  isDark = true, // 🆕 V4: prop para modo claro/oscuro (default: true para no romper)
  registrosFiltrados,
  totalReceitas,
  totalDespesas,
  saldo,
  vendasStats,
  alugueisStats,
  totaisGerais,
  tendencia,
  // ── sparkline props ─────────────────────────────────────────────────────
  receitasSparkline,
  despesasSparkline,
  vendasSparkline,
  alugueisSparkline,
  saldoSparkline,
}) => {
  // ── safe values ──────────────────────────────────────────────────────────
  const receitas      = safeNum(totalReceitas);
  const despesas      = safeNum(totalDespesas);
  const saldoSeg      = safeNum(saldo);
  const vendasMesVal  = safeNum(vendasStats?.mesValor);
  const alugMesVal    = safeNum(alugueisStats?.mesValor);
  const vendasCount   = safeNum(vendasStats?.mes);
  const alugCount     = safeNum(alugueisStats?.ativos);

  const registros     = safeArr(registrosFiltrados);
  const recCnt        = registros.filter(r => r.type === "receita" || r.type === "income").length;
  const despCnt       = registros.filter(r => r.type === "despesa" || r.type === "expense").length;

  const saldoGeral    = safeNum(totaisGerais?.saldoGeral, saldoSeg);
  const totalReg      = safeNum(totaisGerais?.totalRegistros, recCnt + despCnt);

  const direcao       = tendencia?.direcao || "stable";
  const pctTend       = safeNum(tendencia?.percentual, 0);

  const saldoPositive = saldoSeg >= 0;
  const geralPositive = saldoGeral >= 0;

  // ── Colores adaptativos según modo ──────────────────────────────────────
  // Textos de etiquetas (títulos)
  const labelText = isDark ? "text-gray-400" : "text-gray-600";
  // Subtítulos
  const subText = isDark ? "text-gray-600" : "text-gray-500";
  // Texto de valor principal
  const valueEmerald = isDark ? "text-emerald-400" : "text-emerald-600";
  const valueRed = isDark ? "text-red-400" : "text-red-600";
  const valueViolet = isDark ? "text-violet-400" : "text-violet-600";
  const valueBlue = isDark ? "text-blue-400" : "text-blue-600";
  const valueVioletLight = isDark ? "text-violet-300" : "text-violet-600";
  // Subtítulos de color
  const subEmerald = isDark ? "text-emerald-600" : "text-emerald-500";
  const subRed = isDark ? "text-red-600" : "text-red-500";
  const subViolet = isDark ? "text-violet-600" : "text-violet-500";
  const subBlue = isDark ? "text-blue-600" : "text-blue-500";
  const subGray = isDark ? "text-gray-600" : "text-gray-500";
  // Separadores
  const dotColor = isDark ? "text-white/10" : "text-gray-300";
  // Badges
  const badgeEmerald = isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700";
  const badgeRed = isDark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700";
  const badgeViolet = isDark ? "bg-violet-500/15 text-violet-400" : "bg-violet-100 text-violet-700";
  const badgeBlue = isDark ? "bg-blue-500/15 text-blue-400" : "bg-blue-100 text-blue-700";
  const badgeVioletAcc = isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-100 text-violet-700";
  const badgeRedAcc = isDark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700";
  // Fondos de iconos
  const iconEmeraldBg = isDark ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.12)";
  const iconRedBg = isDark ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.12)";
  const iconVioletBg = isDark ? "rgba(139,92,246,0.15)" : "rgba(139,92,246,0.12)";
  const iconBlueBg = isDark ? "rgba(59,130,246,0.15)" : "rgba(59,130,246,0.12)";
  const iconVioletAccBg = isDark ? "rgba(167,139,250,0.15)" : "rgba(167,139,250,0.12)";

  // ── grid ─────────────────────────────────────────────────────────────────
  const gridClass = isMobile
    ? "grid-cols-2 gap-2"
    : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4";

  return (
    <div className={`grid ${gridClass} mb-6`}>

      {/* ══ 1. RECEITAS ══════════════════════════════════════════════════════ */}
      <Card
        delay={0.04} isMobile={isMobile} isDark={isDark}
        glassFromDark="rgba(16,185,129,0.08)" glassToDark="rgba(16,185,129,0.03)"
        borderColorDark="rgba(16,185,129,0.15)" shadowColorDark="rgba(16,185,129,0.12)"
        bgFromLight="#f0fdf4" bgToLight="#ecfdf5"
        borderColorLight="rgba(16,185,129,0.18)" shadowColorLight="rgba(16,185,129,0.06)"
      >
        {/* top row */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <IconCircle Icon={TrendingUp} color="#10b981" bg={iconEmeraldBg} isMobile={isMobile} />
            <span className={`${labelText} font-medium truncate ${isMobile ? "text-[10px]" : "text-xs"}`}>
              Receitas
            </span>
          </div>
          <CountBadge
            count={recCnt}
            colorClass={badgeEmerald}
            isMobile={isMobile}
          />
        </div>

        {/* value */}
        <CountingValue
          value={receitas}
          compact={isMobile}
          className={`block font-bold tabular-nums ${valueEmerald} leading-tight
                      ${isMobile ? "text-sm" : "text-xl"}`}
        />

        {/* sparkline + trend */}
        {receitasSparkline && (
          <div className="flex items-end justify-between mt-2">
            <Sparkline data={receitasSparkline} color="#10b981" isMobile={isMobile} isDark={isDark} />
            <TrendBadge direction={direcao} percent={pctTend} isMobile={isMobile} isDark={isDark} />
          </div>
        )}

        {/* subtitle */}
        <p className={`${subEmerald} mt-1 ${isMobile ? "text-[8px]" : "text-[10px]"}`}>
          {recCnt === 1 ? "1 entrada" : `${recCnt} entradas`}
        </p>
      </Card>

      {/* ══ 2. DESPESAS ══════════════════════════════════════════════════════ */}
      <Card
        delay={0.09} isMobile={isMobile} isDark={isDark}
        glassFromDark="rgba(239,68,68,0.08)" glassToDark="rgba(239,68,68,0.03)"
        borderColorDark="rgba(239,68,68,0.15)" shadowColorDark="rgba(239,68,68,0.12)"
        bgFromLight="#fef2f2" bgToLight="#fef5f5"
        borderColorLight="rgba(239,68,68,0.18)" shadowColorLight="rgba(239,68,68,0.06)"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <IconCircle Icon={TrendingDown} color="#ef4444" bg={iconRedBg} isMobile={isMobile} />
            <span className={`${labelText} font-medium truncate ${isMobile ? "text-[10px]" : "text-xs"}`}>
              Despesas
            </span>
          </div>
          <CountBadge count={despCnt} colorClass={badgeRed} isMobile={isMobile} />
        </div>

        <CountingValue
          value={despesas}
          compact={isMobile}
          className={`block font-bold tabular-nums ${valueRed} leading-tight
                      ${isMobile ? "text-sm" : "text-xl"}`}
        />

        {despesasSparkline && (
          <div className="flex items-end justify-between mt-2">
            <Sparkline data={despesasSparkline} color="#ef4444" isMobile={isMobile} isDark={isDark} />
          </div>
        )}

        <p className={`${subRed} mt-1 ${isMobile ? "text-[8px]" : "text-[10px]"}`}>
          {despCnt === 1 ? "1 saída" : `${despCnt} saídas`}
        </p>
      </Card>

      {/* ══ 3. SALDO ═════════════════════════════════════════════════════════ */}
      <Card
        delay={0.14} isMobile={isMobile} isDark={isDark}
        glassFromDark={saldoPositive ? "rgba(16,185,129,0.06)" : "rgba(239,68,68,0.06)"}
        glassToDark="rgba(255,255,255,0.02)"
        borderColorDark={saldoPositive ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)"}
        shadowColorDark={saldoPositive ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)"}
        bgFromLight={saldoPositive ? "#f0fdf4" : "#fef2f2"}
        bgToLight={saldoPositive ? "#ecfdf5" : "#fef5f5"}
        borderColorLight={saldoPositive ? "rgba(16,185,129,0.18)" : "rgba(239,68,68,0.18)"}
        shadowColorLight={saldoPositive ? "rgba(16,185,129,0.06)" : "rgba(239,68,68,0.06)"}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <IconCircle
              Icon={Wallet}
              color={saldoPositive ? "#10b981" : "#ef4444"}
              bg={saldoPositive ? iconEmeraldBg : iconRedBg}
              isMobile={isMobile}
            />
            <span className={`${labelText} font-medium truncate ${isMobile ? "text-[10px]" : "text-xs"}`}>
              Saldo
            </span>
          </div>
          <TrendBadge direction={direcao} percent={pctTend} isMobile={isMobile} isDark={isDark} />
        </div>

        <CountingValue
          value={saldoSeg}
          compact={isMobile}
          className={`block font-bold tabular-nums leading-tight
                      ${isMobile ? "text-sm" : "text-xl"}
                      ${saldoPositive ? valueEmerald : valueRed}`}
        />

        {saldoSparkline && (
          <div className="flex items-end justify-between mt-2">
            <Sparkline
              data={saldoSparkline}
              color={saldoPositive ? "#10b981" : "#ef4444"}
              isMobile={isMobile}
              isDark={isDark}
            />
          </div>
        )}

        <p className={`${subGray} mt-1 ${isMobile ? "text-[8px]" : "text-[10px]"}`}>
          no mês
        </p>
      </Card>

      {/* ══ 4. VENDAS ════════════════════════════════════════════════════════ */}
      <Card
        delay={0.19} isMobile={isMobile} isDark={isDark}
        glassFromDark="rgba(139,92,246,0.08)" glassToDark="rgba(139,92,246,0.03)"
        borderColorDark="rgba(139,92,246,0.15)" shadowColorDark="rgba(139,92,246,0.12)"
        bgFromLight="#f5f3ff" bgToLight="#f8f7ff"
        borderColorLight="rgba(139,92,246,0.18)" shadowColorLight="rgba(139,92,246,0.06)"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <IconCircle Icon={Home} color="#8b5cf6" bg={iconVioletBg} isMobile={isMobile} />
            <span className={`${labelText} font-medium truncate ${isMobile ? "text-[10px]" : "text-xs"}`}>
              Vendas
            </span>
          </div>
          <CountBadge count={vendasCount} colorClass={badgeViolet} isMobile={isMobile} />
        </div>

        <CountingValue
          value={vendasMesVal}
          compact={isMobile}
          className={`block font-bold tabular-nums ${valueViolet} leading-tight
                      ${isMobile ? "text-sm" : "text-xl"}`}
        />

        {vendasSparkline && (
          <div className="flex items-end justify-between mt-2">
            <Sparkline data={vendasSparkline} color="#8b5cf6" isMobile={isMobile} isDark={isDark} />
          </div>
        )}

        <p className={`${subViolet} mt-1 ${isMobile ? "text-[8px]" : "text-[10px]"}`}>
          {vendasCount === 1 ? "1 imóvel" : `${vendasCount} imóveis`}
        </p>
      </Card>

      {/* ══ 5. ALUGUÉIS ══════════════════════════════════════════════════════ */}
      <Card
        delay={0.24} isMobile={isMobile} isDark={isDark}
        glassFromDark="rgba(59,130,246,0.08)" glassToDark="rgba(59,130,246,0.03)"
        borderColorDark="rgba(59,130,246,0.15)" shadowColorDark="rgba(59,130,246,0.12)"
        bgFromLight="#eff6ff" bgToLight="#f0f9ff"
        borderColorLight="rgba(59,130,246,0.18)" shadowColorLight="rgba(59,130,246,0.06)"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <IconCircle Icon={Key} color="#3b82f6" bg={iconBlueBg} isMobile={isMobile} />
            <span className={`${labelText} font-medium truncate ${isMobile ? "text-[10px]" : "text-xs"}`}>
              Aluguéis
            </span>
          </div>
          <CountBadge count={alugCount} colorClass={badgeBlue} isMobile={isMobile} />
        </div>

        <CountingValue
          value={alugMesVal}
          compact={isMobile}
          className={`block font-bold tabular-nums ${valueBlue} leading-tight
                      ${isMobile ? "text-sm" : "text-xl"}`}
        />

        {alugueisSparkline && (
          <div className="flex items-end justify-between mt-2">
            <Sparkline data={alugueisSparkline} color="#3b82f6" isMobile={isMobile} isDark={isDark} />
          </div>
        )}

        <p className={`${subBlue} mt-1 ${isMobile ? "text-[8px]" : "text-[10px]"}`}>
          {alugCount === 1 ? "1 contrato" : `${alugCount} contratos`}
        </p>
      </Card>

      {/* ══ 6. TOTAL GERAL — PREMIUM CARD ═══════════════════════════════════ */}
      <Card
        delay={0.29} isMobile={isMobile} isDark={isDark} colSpan2={isMobile}
        glassFromDark={geralPositive ? "rgba(99,102,241,0.10)" : "rgba(239,68,68,0.10)"}
        glassToDark={geralPositive ? "rgba(168,85,247,0.05)" : "rgba(239,68,68,0.04)"}
        borderColorDark={geralPositive ? "rgba(99,102,241,0.20)" : "rgba(239,68,68,0.20)"}
        shadowColorDark={geralPositive ? "rgba(99,102,241,0.20)" : "rgba(239,68,68,0.15)"}
        bgFromLight={geralPositive ? "#faf5ff" : "#fef2f2"}
        bgToLight={geralPositive ? "#fdf4ff" : "#fef5f5"}
        borderColorLight={geralPositive ? "rgba(99,102,241,0.20)" : "rgba(239,68,68,0.20)"}
        shadowColorLight={geralPositive ? "rgba(99,102,241,0.08)" : "rgba(239,68,68,0.08)"}
      >
        {/* top row */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <IconCircle
              Icon={Sparkles}
              color={geralPositive ? "#a78bfa" : "#ef4444"}
              bg={geralPositive ? iconVioletAccBg : iconRedBg}
              isMobile={isMobile}
            />
            <span className={`${labelText} font-medium truncate ${isMobile ? "text-[10px]" : "text-xs"}`}>
              Total Geral
            </span>
          </div>
          <motion.span
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ ...springBadge, delay: 0.35 }}
            className={`font-bold rounded-full tracking-tight
                        ${isMobile ? "text-[7px] px-1.5 py-0.5" : "text-[9px] px-2 py-0.5"}
                        ${geralPositive ? badgeVioletAcc : badgeRedAcc}`}
          >
            acumulado
          </motion.span>
        </div>

        {/* value */}
        <CountingValue
          value={saldoGeral}
          compact={isMobile}
          className={`block font-extrabold tabular-nums leading-tight
                      ${isMobile ? "text-base" : "text-2xl"}
                      ${geralPositive ? valueVioletLight : valueRed}`}
          style={{ letterSpacing: "-0.03em" }}
        />

        {/* Progress bar receitas vs despesas */}
        <ProgressBar
          receitas={safeNum(totaisGerais?.totalReceitas, receitas)}
          despesas={safeNum(totaisGerais?.totalDespesas, despesas)}
          isDark={isDark}
        />

        {/* progress labels */}
        <div className={`flex justify-between mt-1 ${isMobile ? "text-[7px]" : "text-[9px]"} ${subText}`}>
          <span className={subEmerald}>
            receitas {safeNum(totaisGerais?.totalReceitas, receitas) > 0
              ? ((safeNum(totaisGerais?.totalReceitas, receitas) /
                 Math.max(safeNum(totaisGerais?.totalReceitas, receitas) + safeNum(totaisGerais?.totalDespesas, despesas), 1)) * 100).toFixed(0)
              : 0}%
          </span>
          <span className={subRed}>
            despesas {safeNum(totaisGerais?.totalDespesas, despesas) > 0
              ? ((safeNum(totaisGerais?.totalDespesas, despesas) /
                 Math.max(safeNum(totaisGerais?.totalReceitas, receitas) + safeNum(totaisGerais?.totalDespesas, despesas), 1)) * 100).toFixed(0)
              : 0}%
          </span>
        </div>

        {/* subtitle */}
        <p className={`${subText} mt-1.5 flex flex-wrap gap-x-1.5 ${isMobile ? "text-[7px]" : "text-[9px]"}`}>
          <span>{totalReg} registros</span>
          <span className={dotColor}>•</span>
          <span>{vendasCount} {vendasCount === 1 ? "venda" : "vendas"}</span>
          <span className={dotColor}>•</span>
          <span>{alugCount} {alugCount === 1 ? "aluguel" : "aluguéis"}</span>
        </p>
      </Card>

    </div>
  );
};

export default SummaryCards;